import { useEffect, useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

export default function HeroNew() {
  const [counts, setCounts] = useState({ speed: 0, opex: 0, csat: 0, support: 0 });
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineFade, setTaglineFade] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const targets = { speed: 50, opex: 20, csat: 35, support: 24 };
    const steps = 60;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const p = step / steps;
      setCounts({
        speed: Math.floor(targets.speed * p),
        opex: Math.floor(targets.opex * p),
        csat: Math.floor(targets.csat * p),
        support: Math.floor(targets.support * p),
      });
      if (step >= steps) { clearInterval(timer); setCounts(targets); }
    }, 2000 / steps);
    return () => clearInterval(timer);
  }, []);

  const taglines = {
    de: [
      'Automatisierung, die wirklich funktioniert.',
      'DSGVO-konform. Sicher. Skalierbar.',
      'Weniger Aufwand. Mehr Kundenzufriedenheit.',
      'Ihre KI — Ihre Regeln.',
    ],
    en: [
      'Automation that actually works.',
      'GDPR-compliant. Secure. Scalable.',
      'Less effort. More customer satisfaction.',
      'Your AI — your rules.',
    ],
  };

  useEffect(() => {
    const list = taglines[language];
    const interval = setInterval(() => {
      setTaglineFade(false);
      setTimeout(() => {
        setTaglineIndex(prev => (prev + 1) % list.length);
        setTaglineFade(true);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, [language]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const content = {
    de: {
      badge: 'KI-Plattform für Telcos',
      headline: 'KI-Power.',
      headlineHighlight: 'Volle Kontrolle.',
      subtext: 'Von der Kundenanfrage bis zur Rechnungsverarbeitung — vollständig automatisiert, sicher und DSGVO-konform.',
      ctaDemo: 'Kostenlose Demo',
      ctaLearn: 'Mehr erfahren',
      stats: [
        { label: 'schnellere Tickets' },
        { label: 'OPEX-Reduktion' },
        { label: 'CSAT-Steigerung' },
        { label: 'Auto-Support' }
      ]
    },
    en: {
      badge: 'AI Platform for Telcos',
      headline: 'AI that works for you.',
      headlineHighlight: 'Control that stays with you.',
      subtext: 'From customer inquiry to invoice processing — fully automated, secure, and GDPR-compliant.',
      ctaDemo: 'Free Demo',
      ctaLearn: 'Learn More',
      stats: [
        { label: 'faster tickets' },
        { label: 'OPEX reduction' },
        { label: 'CSAT increase' },
        { label: 'auto support' }
      ]
    }
  };

  const t = content[language];

  return (
    <section id="hero" className="relative min-h-screen flex items-center bg-black overflow-hidden">
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(rgba(59,130,246,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.4) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />
      {/* Blue glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
        <div className="flex flex-col items-center text-center gap-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 border border-blue-500/40 rounded-full bg-blue-500/10">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
            <span className="text-blue-400 text-xs font-semibold tracking-widest uppercase">{t.badge}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-tight tracking-tight max-w-4xl">
            {t.headline}{' '}
            <span className="text-blue-500">{t.headlineHighlight}</span>
          </h1>

          {/* Animated tagline */}
          <div className="h-8 flex items-center justify-center">
            <p
              className="text-sm sm:text-base font-medium tracking-wide text-blue-300/90 transition-all duration-400"
              style={{ opacity: taglineFade ? 1 : 0, transform: taglineFade ? 'translateY(0)' : 'translateY(6px)' }}
            >
              — {taglines[language][taglineIndex]} —
            </p>
          </div>

          <p className="text-base sm:text-lg text-white/60 max-w-2xl leading-relaxed">
            {t.subtext}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <a
              href="mailto:info@teliq.one"
              className="px-8 py-4 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-500 transition-all hover:shadow-lg hover:shadow-blue-600/30 whitespace-nowrap cursor-pointer text-sm"
            >
              {t.ctaDemo}
            </a>
            <button
              onClick={() => scrollTo('solution')}
              className="px-8 py-4 bg-white/5 border border-white/20 text-white font-semibold rounded-md hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer text-sm"
            >
              {t.ctaLearn}
            </button>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-3xl mt-6">
            {[
              { value: `${counts.speed}%` },
              { value: `−${counts.opex}%` },
              { value: `+${counts.csat}%` },
              { value: `${counts.support}/7` }
            ].map((stat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 hover:border-blue-500/40 transition-all">
                <div className="text-2xl sm:text-3xl font-black text-blue-500 mb-1">{stat.value}</div>
                <div className="text-xs text-white/50">{t.stats[i].label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
    </section>
  );
}
